/**
 * BrochureHotelsEditor — operator-facing editor for the hotel sections
 * that live inside a Pollen Dots brochure. Each brochure can carry an
 * arbitrary list of HOTEL cards; each hotel carries an arbitrary list of
 * ROOM categories; each room carries its own photo/video gallery.
 *
 * The component receives the brochure object + an `onSaved` callback the
 * studio uses to swap in the freshest version of the doc after each API
 * call. All persistence is via the `/api/pollen/brochures/{bid}/hotels`
 * tree (see `pollen_brochures.py`).
 *
 * Kept deliberately compact — sub-components only where they would
 * otherwise add real value. No drag-reorder yet; hotels and rooms render
 * in insertion order, which matches the operator's mental model.
 */
import React, { useRef, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  ChevronDown, ChevronUp, Hotel as HotelIcon, Image as ImageIcon, Link2,
  Loader2, Plus, Trash2, Upload, Video,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const t = localStorage.getItem('session_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const mediaPreview = (bid, hid, rid, mid) => {
  const t = encodeURIComponent(localStorage.getItem('session_token') || '');
  return `${API}/api/pollen/brochures/${bid}/hotels/${hid}/rooms/${rid}/media-preview/${mid}?auth=${t}`;
};

export default function BrochureHotelsEditor({ brochure, onSaved }) {
  const [busy, setBusy] = useState('');
  const [expanded, setExpanded] = useState({}); // { [hotelId]: bool }

  const call = async (path, opts = {}, busyKey = '') => {
    setBusy(busyKey);
    try {
      const r = await fetch(`${API}${path}`, {
        credentials: 'include',
        headers: { ...authHeaders(), ...(opts.headers || {}) },
        ...opts,
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Request failed');
      }
      return r.status === 204 ? null : await r.json();
    } finally {
      setBusy('');
    }
  };

  const refreshFromBrochure = async () => {
    // Re-fetch the brochure so the editor renders the freshest hotel tree.
    const r = await fetch(`${API}/api/pollen/brochures/${brochure.id}`, {
      credentials: 'include', headers: authHeaders(),
    });
    if (r.ok) onSaved && onSaved(await r.json());
  };

  const addHotel = async () => {
    try {
      await call(`/api/pollen/brochures/${brochure.id}/hotels`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: 'New hotel', intro: '' }),
      }, 'add-hotel');
      await refreshFromBrochure();
    } catch (e) { toast.error(e.message); }
  };

  const deleteHotel = async (hid) => {
    if (!window.confirm('Remove this hotel and all its rooms?')) return;
    try {
      await call(`/api/pollen/brochures/${brochure.id}/hotels/${hid}`, {
        method: 'DELETE',
      }, `del-hotel-${hid}`);
      await refreshFromBrochure();
    } catch (e) { toast.error(e.message); }
  };

  const patchHotel = async (hid, fields) => {
    try {
      await call(`/api/pollen/brochures/${brochure.id}/hotels/${hid}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fields),
      }, `save-hotel-${hid}`);
      await refreshFromBrochure();
    } catch (e) { toast.error(e.message); }
  };

  const hotels = brochure.hotels || [];

  return (
    <div className="space-y-3 border-2 border-sky-200 bg-sky-50/40 rounded-lg p-3"
         data-testid="bro-hotels-editor">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-semibold text-sky-900 flex items-center gap-1.5">
          <HotelIcon className="w-3.5 h-3.5" /> Hotels &amp; Room categories
          <span className="text-[10px] text-muted-foreground font-normal">
            (optional — add per-hotel rooms with photos &amp; videos)
          </span>
        </Label>
        <Button size="sm"
                disabled={busy === 'add-hotel'}
                onClick={addHotel}
                className="h-8 bg-sky-700 hover:bg-sky-800 text-white"
                data-testid="bro-add-hotel">
          {busy === 'add-hotel'
            ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
            : <Plus className="w-3.5 h-3.5 mr-1" />}
          Add hotel
        </Button>
      </div>

      {hotels.length === 0 && (
        <p className="text-[11px] text-muted-foreground italic">
          No hotel sections yet. Add one to start grouping room categories
          and their media.
        </p>
      )}

      {hotels.map((h) => (
        <HotelCard
          key={h.id}
          brochureId={brochure.id}
          hotel={h}
          expanded={!!expanded[h.id]}
          onToggle={() => setExpanded(s => ({ ...s, [h.id]: !s[h.id] }))}
          busy={busy}
          onPatch={(fields) => patchHotel(h.id, fields)}
          onDelete={() => deleteHotel(h.id)}
          onRefresh={refreshFromBrochure}
          call={call}
        />
      ))}
    </div>
  );
}

function HotelCard({ brochureId, hotel, expanded, onToggle, busy, onPatch,
                    onDelete, onRefresh, call }) {
  const [name, setName] = useState(hotel.name || '');
  const [intro, setIntro] = useState(hotel.intro || '');

  const addRoom = async () => {
    try {
      await call(`/api/pollen/brochures/${brochureId}/hotels/${hotel.id}/rooms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: 'Room category', description: '' }),
      }, `add-room-${hotel.id}`);
      await onRefresh();
    } catch (e) { toast.error(e.message); }
  };

  const rooms = hotel.rooms || [];

  return (
    <div className="rounded-lg border bg-white">
      <div className="flex items-center gap-2 p-2">
        <button type="button" onClick={onToggle}
                className="h-7 w-7 flex items-center justify-center rounded hover:bg-slate-100"
                data-testid={`bro-hotel-toggle-${hotel.id}`}>
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        <Input value={name} onChange={e => setName(e.target.value)}
               onBlur={() => name !== hotel.name && onPatch({ name })}
               placeholder="Hotel name"
               className="h-8 text-sm font-semibold flex-1"
               data-testid={`bro-hotel-name-${hotel.id}`} />
        <Badge variant="secondary" className="text-[10px]">
          {rooms.length} {rooms.length === 1 ? 'room' : 'rooms'}
        </Badge>
        <Button size="sm" variant="ghost" className="h-8 text-rose-700 hover:bg-rose-50"
                onClick={onDelete}
                disabled={busy === `del-hotel-${hotel.id}`}
                data-testid={`bro-hotel-delete-${hotel.id}`}>
          {busy === `del-hotel-${hotel.id}`
            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
            : <Trash2 className="w-3.5 h-3.5" />}
        </Button>
      </div>

      {expanded && (
        <div className="p-3 pt-1 space-y-3 border-t bg-slate-50/30">
          <div>
            <Label className="text-[10px] text-muted-foreground">
              Hotel intro (optional — one short paragraph)
            </Label>
            <Textarea rows={2} value={intro}
                      onChange={e => setIntro(e.target.value)}
                      onBlur={() => intro !== (hotel.intro || '') && onPatch({ intro })}
                      placeholder="A short note about this hotel — feel, location, why you love it."
                      className="text-xs"
                      data-testid={`bro-hotel-intro-${hotel.id}`} />
          </div>

          {rooms.map((r) => (
            <RoomCard
              key={r.id}
              brochureId={brochureId}
              hotelId={hotel.id}
              room={r}
              busy={busy}
              call={call}
              onRefresh={onRefresh}
            />
          ))}

          <Button size="sm" variant="outline"
                  onClick={addRoom}
                  disabled={busy === `add-room-${hotel.id}`}
                  className="w-full h-9 border-dashed"
                  data-testid={`bro-add-room-${hotel.id}`}>
            {busy === `add-room-${hotel.id}`
              ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
              : <Plus className="w-3.5 h-3.5 mr-1" />}
            Add room category
          </Button>
        </div>
      )}
    </div>
  );
}

function RoomCard({ brochureId, hotelId, room, busy, call, onRefresh }) {
  const [name, setName] = useState(room.name || '');
  const [description, setDescription] = useState(room.description || '');
  const [linkInput, setLinkInput] = useState({ kind: 'youtube', url: '' });
  const fileRef = useRef(null);

  const base = `/api/pollen/brochures/${brochureId}/hotels/${hotelId}/rooms/${room.id}`;

  const patchRoom = async (fields) => {
    try {
      await call(base, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fields),
      }, `save-room-${room.id}`);
      await onRefresh();
    } catch (e) { toast.error(e.message); }
  };

  const deleteRoom = async () => {
    if (!window.confirm('Remove this room category and all its media?')) return;
    try {
      await call(base, { method: 'DELETE' }, `del-room-${room.id}`);
      await onRefresh();
    } catch (e) { toast.error(e.message); }
  };

  const uploadFiles = async (filesList) => {
    const files = Array.from(filesList || []);
    if (!files.length) return;
    for (const f of files) {
      const fd = new FormData();
      fd.append('file', f);
      try {
        await call(`${base}/media`, { method: 'POST', body: fd },
                   `up-room-${room.id}`);
      } catch (e) { toast.error(`${f.name}: ${e.message}`); }
    }
    if (fileRef.current) fileRef.current.value = '';
    await onRefresh();
  };

  const addLink = async () => {
    if (!linkInput.url.trim()) { toast.error('Paste a video URL first'); return; }
    try {
      await call(`${base}/links`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(linkInput),
      }, `link-room-${room.id}`);
      setLinkInput({ kind: 'youtube', url: '' });
      await onRefresh();
    } catch (e) { toast.error(e.message); }
  };

  const removeMedia = async (mid) => {
    try {
      await call(`${base}/media/${mid}`, { method: 'DELETE' }, `del-media-${mid}`);
      await onRefresh();
    } catch (e) { toast.error(e.message); }
  };

  const media = room.media || [];

  return (
    <div className="rounded border bg-white p-2 space-y-2">
      <div className="flex items-center gap-2">
        <Input value={name} onChange={e => setName(e.target.value)}
               onBlur={() => name !== (room.name || '') && patchRoom({ name })}
               placeholder="Room category (e.g. Deluxe King, Suite)"
               className="h-8 text-xs font-medium flex-1"
               data-testid={`bro-room-name-${room.id}`} />
        <Button size="sm" variant="ghost"
                onClick={deleteRoom}
                disabled={busy === `del-room-${room.id}`}
                className="h-8 text-rose-700 hover:bg-rose-50"
                data-testid={`bro-room-delete-${room.id}`}>
          {busy === `del-room-${room.id}`
            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
            : <Trash2 className="w-3.5 h-3.5" />}
        </Button>
      </div>

      <Textarea rows={2} value={description}
                onChange={e => setDescription(e.target.value)}
                onBlur={() => description !== (room.description || '') && patchRoom({ description })}
                placeholder="Short description — view, bed, what makes it special."
                className="text-xs"
                data-testid={`bro-room-desc-${room.id}`} />

      {/* Media grid */}
      <div className="grid grid-cols-3 gap-1.5">
        {media.map(m => (
          <div key={m.id} className="relative rounded border overflow-hidden">
            {m.kind === 'photo' && m.storage_path && (
              <img src={mediaPreview(brochureId, hotelId, room.id, m.id)}
                   alt="" className="w-full h-20 object-cover block" />
            )}
            {m.kind === 'video' && (
              <div className="w-full h-20 bg-slate-900 text-white flex items-center justify-center">
                <Video className="w-5 h-5" />
              </div>
            )}
            {(m.kind === 'youtube' || m.kind === 'vimeo') && (
              <div className="w-full h-20 bg-rose-900 text-white flex flex-col items-center justify-center text-[9px]">
                <Link2 className="w-4 h-4 mb-0.5" />
                {m.kind}
              </div>
            )}
            <button type="button"
                    onClick={() => removeMedia(m.id)}
                    className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-white/90 hover:bg-rose-50 text-rose-700 flex items-center justify-center"
                    data-testid={`bro-room-media-remove-${m.id}`}>
              <Trash2 className="w-2.5 h-2.5" />
            </button>
          </div>
        ))}
      </div>

      {/* Upload + link */}
      <div className="flex flex-col gap-1.5">
        <input type="file" multiple accept="image/*,video/*" ref={fileRef}
               className="hidden"
               onChange={e => uploadFiles(e.target.files)} />
        <div className="flex gap-1.5">
          <Button size="sm" variant="outline"
                  onClick={() => fileRef.current?.click()}
                  disabled={busy === `up-room-${room.id}`}
                  className="flex-1 h-8 text-[11px]"
                  data-testid={`bro-room-upload-${room.id}`}>
            {busy === `up-room-${room.id}`
              ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              : <Upload className="w-3 h-3 mr-1" />}
            Upload
          </Button>
        </div>
        <div className="flex items-center gap-1.5">
          <select className="h-8 text-[11px] border rounded px-1 bg-white"
                  value={linkInput.kind}
                  onChange={e => setLinkInput({ ...linkInput, kind: e.target.value })}
                  data-testid={`bro-room-link-kind-${room.id}`}>
            <option value="youtube">YT</option>
            <option value="vimeo">Vimeo</option>
          </select>
          <Input className="h-8 flex-1 text-[11px]"
                 placeholder="Paste video URL"
                 value={linkInput.url}
                 onChange={e => setLinkInput({ ...linkInput, url: e.target.value })}
                 data-testid={`bro-room-link-url-${room.id}`} />
          <Button size="sm" onClick={addLink}
                  disabled={busy === `link-room-${room.id}`}
                  className="h-8 text-[11px] bg-sky-700 hover:bg-sky-800"
                  data-testid={`bro-room-link-add-${room.id}`}>
            <Link2 className="w-3 h-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}
